/**
 * ModMatrix — the panel that lets the user wire modulations.
 *
 * Toggled by a topbar button; slides in from the right. Top of the panel has
 * an "Add connection" form (source dropdown + target dropdown + go). The rest
 * is a list of existing connections with a depth slider and a delete button
 * per row. Subscribes to the ModRouter to re-render on any change.
 */

import type { ModRouter } from '@ac-slayer/synth-core';
import type { ModConnection, ModCurve } from '@ac-slayer/synth-core';
import { MOD_CURVES } from '@ac-slayer/synth-core';
import {
  enumerateTargets,
  type GlobalTargets,
  type ModTarget,
} from '@ac-slayer/synth-core';
import type { VoiceTemplate } from '@ac-slayer/synth-core';
import type { MacroBank } from '@ac-slayer/synth-core';
import { DepthSlider } from '../components/DepthSlider';

export interface ModMatrixOptions {
  readonly router: ModRouter;
  readonly template: VoiceTemplate;
  /** Optional global-scope modules (effects) whose params become mod targets. */
  readonly globals?: GlobalTargets;
  /** Optional macro bank — when supplied, macro values become mod targets. */
  readonly macroBank?: MacroBank;
}

export class ModMatrix {
  readonly element: HTMLElement;
  private readonly router: ModRouter;
  /**
   * Current target list — refreshed via `setTargets()` whenever the
   * effects chain mutates (add / remove / reorder), since duplicate
   * effects make labels and target IDs depend on the live chain.
   */
  private targets: readonly ModTarget[];
  private readonly unsubscribe: () => void;

  private readonly listEl: HTMLElement;
  private readonly sourceSelect: HTMLSelectElement;
  private readonly targetSelect: HTMLSelectElement;
  private readonly addBtn: HTMLButtonElement;
  private readonly emptyEl: HTMLElement;

  private open = false;

  constructor(opts: ModMatrixOptions) {
    this.router = opts.router;
    this.targets = enumerateTargets(opts.template, opts.globals, opts.macroBank);

    this.element = document.createElement('aside');
    this.element.className = 'mod-matrix';
    this.element.setAttribute('aria-label', 'Modulation matrix');
    this.element.hidden = true;

    const header = document.createElement('header');
    header.className = 'mod-matrix-header';
    header.innerHTML = `<h2 class="mod-matrix-title">Mod Matrix</h2>`;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn btn-sm';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => this.setOpen(false));
    header.appendChild(closeBtn);

    this.element.appendChild(header);

    // Add-connection form
    const form = document.createElement('div');
    form.className = 'mod-matrix-form';

    this.sourceSelect = document.createElement('select');
    this.sourceSelect.setAttribute('aria-label', 'Source');
    this.sourceSelect.className = 'mod-matrix-select';

    this.targetSelect = document.createElement('select');
    this.targetSelect.setAttribute('aria-label', 'Target');
    this.targetSelect.className = 'mod-matrix-select';
    // Options are populated in refreshSources() once the source dropdown is
    // set up — targets are source-dependent (a macro can't target itself), so
    // we have to rebuild the target list whenever the source changes.
    this.sourceSelect.addEventListener('change', () => this.refreshAddTargets());

    this.addBtn = document.createElement('button');
    this.addBtn.type = 'button';
    this.addBtn.className = 'btn btn-sm';
    this.addBtn.textContent = '+ Add';
    this.addBtn.addEventListener('click', () => this.handleAdd());

    const arrow = document.createElement('span');
    arrow.className = 'mod-matrix-arrow';
    arrow.textContent = '→';

    form.append(this.sourceSelect, arrow, this.targetSelect, this.addBtn);
    this.element.appendChild(form);

    // List + empty state
    this.listEl = document.createElement('div');
    this.listEl.className = 'mod-matrix-list';
    this.element.appendChild(this.listEl);

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'mod-matrix-empty label';
    this.emptyEl.textContent = 'No modulations. Pick a source and target above.';
    this.element.appendChild(this.emptyEl);

    this.refreshSources();
    this.refreshAddTargets();
    this.render();
    // Only rebuild rows when the connection LIST structure changes —
    // add / remove / reorder. Depth and rangeMul updates (which fire
    // on every drag sample) hit the router's emit too, but rebuilding
    // the list then would destroy the slider element mid-drag and
    // kill the gesture. Cheap fingerprint compare avoids that.
    this.unsubscribe = this.router.subscribe(() => {
      this.refreshSources();
      this.refreshAddTargets();
      const next = this.router.listConnections();
      if (sameConnectionShape(this.renderedConnections, next)) {
        // Structure unchanged — nothing to rebuild. Sliders keep
        // their DOM node, drag stays alive.
        return;
      }
      this.render();
    });
  }

  /**
   * Snapshot of which connections + source/target pairs the list last
   * rendered. Compared against the router's current list on every
   * emit to decide whether a full rebuild is needed.
   */
  private renderedConnections: ReadonlyArray<{
    id: string;
    source: string;
    target: string;
    param: string;
  }> = [];

  /** Show/hide the panel. */
  setOpen(open: boolean): void {
    this.open = open;
    this.element.hidden = !open;
    this.element.classList.toggle('open', open);
  }

  /** Toggle the panel's open state. */
  toggle(): void {
    this.setOpen(!this.open);
  }

  /**
   * Replace the target list — used when the effects chain mutates and
   * the per-instance labels / target ids change. Refreshes the Add
   * form's target dropdown and re-renders existing rows so their
   * labels match the new chain.
   */
  setTargets(targets: readonly ModTarget[]): void {
    this.targets = targets;
    this.refreshAddTargets();
    this.render();
  }

  dispose(): void {
    this.unsubscribe();
  }

  // ── Internals ────────────────────────────────────────────────────────

  /**
   * Return the targets that are valid for a given source. A macro can't
   * target itself — that would loop forever through the macro wiring path
   * and isn't musically meaningful.
   */
  private allowedTargetsForSource(sourceId: string): readonly ModTarget[] {
    if (!sourceId.startsWith('macro-')) return this.targets;
    return this.targets.filter((t) => t.moduleId !== sourceId);
  }

  /** Rebuild the Add form's target dropdown, filtered for the current source. */
  private refreshAddTargets(): void {
    const sourceId = this.sourceSelect.value;
    const prev = this.targetSelect.value;
    const allowed = this.allowedTargetsForSource(sourceId);
    populateTargetSelect(this.targetSelect, allowed);
    if ([...this.targetSelect.options].some((o) => o.value === prev)) {
      this.targetSelect.value = prev;
    }
  }

  private refreshSources(): void {
    const sources = this.router.listSources();
    const prev = this.sourceSelect.value;
    populateSourceSelect(this.sourceSelect, sources);
    if (sources.length === 0) {
      const o = document.createElement('option');
      o.value = '';
      o.textContent = '(no sources registered)';
      this.sourceSelect.appendChild(o);
    }
    // Restore previous selection if still present.
    if ([...this.sourceSelect.options].some((o) => o.value === prev)) {
      this.sourceSelect.value = prev;
    }
    this.addBtn.disabled = sources.length === 0;
  }

  private handleAdd(): void {
    const sourceId = this.sourceSelect.value;
    if (!sourceId) return;
    const [moduleId, param] = this.targetSelect.value.split('::');
    if (!moduleId || !param) return;
    this.router.addConnection({
      sourceModuleId: sourceId,
      targetModuleId: moduleId,
      targetParam: param,
      depth: 0.5,
    });
  }

  private render(): void {
    const connections = this.router.listConnections();
    this.listEl.innerHTML = '';
    this.emptyEl.hidden = connections.length > 0;

    for (const conn of connections) this.listEl.appendChild(this.renderRow(conn));
    // Record the shape we just painted so the router subscription
    // can skip future re-renders when only depth/rangeMul changed.
    this.renderedConnections = connections.map((c) => ({
      id: c.id,
      source: c.sourceModuleId,
      target: c.targetModuleId,
      param: c.targetParam,
    }));
  }

  private renderRow(conn: ModConnection): HTMLElement {
    const row = document.createElement('div');
    row.className = 'mod-matrix-row';

    const sources = this.router.listSources();
    const sourceLabel =
      sources.find((s) => s.id === conn.sourceModuleId)?.label ??
      `? ${conn.sourceModuleId}`;
    const targetLabel =
      this.targets.find(
        (t) => t.moduleId === conn.targetModuleId && t.param === conn.targetParam,
      )?.label ?? `? ${conn.targetModuleId}.${conn.targetParam}`;

    // Source dropdown — editing rewires the connection via updateConnection.
    const sourceSelect = document.createElement('select');
    sourceSelect.className = 'mod-matrix-select mod-matrix-select-compact';
    sourceSelect.setAttribute('aria-label', 'Source');
    populateSourceSelect(sourceSelect, sources, conn.sourceModuleId);
    // If the stored source no longer exists (e.g. module was removed), show
    // a placeholder so the row is still editable.
    if (!sources.some((s) => s.id === conn.sourceModuleId)) {
      const o = document.createElement('option');
      o.value = conn.sourceModuleId;
      o.textContent = sourceLabel;
      o.selected = true;
      sourceSelect.insertBefore(o, sourceSelect.firstChild);
    }
    sourceSelect.addEventListener('change', () => {
      this.router.updateConnection(conn.id, { sourceModuleId: sourceSelect.value });
    });

    // Target dropdown — encoded as "moduleId::param" like the Add form. Filter
    // by the row's current source so a macro can't be assigned to itself.
    const targetSelect = document.createElement('select');
    targetSelect.className = 'mod-matrix-select mod-matrix-select-compact';
    targetSelect.setAttribute('aria-label', 'Target');
    populateTargetSelect(
      targetSelect,
      this.allowedTargetsForSource(conn.sourceModuleId),
      `${conn.targetModuleId}::${conn.targetParam}`,
    );
    targetSelect.addEventListener('change', () => {
      const [moduleId, param] = targetSelect.value.split('::');
      if (!moduleId || !param) return;
      this.router.updateConnection(conn.id, {
        targetModuleId: moduleId,
        targetParam: param,
      });
    });

    const label = document.createElement('div');
    label.className = 'mod-matrix-row-label';
    const arrow = document.createElement('span');
    arrow.className = 'mod-matrix-arrow';
    arrow.textContent = '→';
    label.append(sourceSelect, arrow, targetSelect);

    const depthReadout = document.createElement('span');
    depthReadout.className = 'mono mod-matrix-depth-readout';
    depthReadout.textContent = formatDepth(conn.depth);

    const slider = new DepthSlider({
      value: conn.depth,
      label: `Depth for ${sourceLabel} → ${targetLabel}`,
      onChange: (v) => {
        this.router.setDepth(conn.id, v);
        depthReadout.textContent = formatDepth(v);
      },
    });

    // Range picker — multiplier on effective mod depth. Slider keeps its
    // full ±1 resolution; range is the "octave" the slider operates in.
    // ×0.1 gives 10× finer control for things like chorus/flanger depth
    // on delay time; ×0.01 for really subtle pitch drift on OSC.Detune.
    const rangeSelect = document.createElement('select');
    rangeSelect.className = 'mod-matrix-range';
    rangeSelect.setAttribute('aria-label', 'Range');
    rangeSelect.title = 'Scale the modulation amount — pick finer values for subtle mods';
    const currentRange = conn.rangeMul ?? 1;
    for (const { value, label } of RANGE_OPTIONS) {
      const o = document.createElement('option');
      o.value = String(value);
      o.textContent = label;
      if (Math.abs(value - currentRange) < 1e-9) o.selected = true;
      rangeSelect.appendChild(o);
    }
    rangeSelect.addEventListener('change', () => {
      const v = parseFloat(rangeSelect.value);
      if (Number.isFinite(v)) this.router.setRangeMul(conn.id, v);
    });

    // Curve picker — only meaningful for macro sources (audio-path
    // mods route through AudioParams and ignore curve). Disabled
    // with a hint when the source isn't a macro so the column
    // stays aligned but the user knows why it's grayed out.
    const curveSelect = document.createElement('select');
    curveSelect.className = 'mod-matrix-curve';
    curveSelect.setAttribute('aria-label', 'Curve');
    const sourceIsMacro = conn.sourceModuleId.startsWith('macro-');
    curveSelect.disabled = !sourceIsMacro;
    curveSelect.title = sourceIsMacro
      ? 'Response shape — how the macro position maps to the target'
      : 'Curves only apply to macro sources';
    const currentCurve: ModCurve = conn.curve ?? 'linear';
    for (const c of MOD_CURVES) {
      const o = document.createElement('option');
      o.value = c;
      o.textContent = curveLabel(c);
      if (c === currentCurve) o.selected = true;
      curveSelect.appendChild(o);
    }
    curveSelect.addEventListener('change', () => {
      const v = curveSelect.value as ModCurve;
      if (MOD_CURVES.includes(v)) this.router.setCurve(conn.id, v);
    });

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'btn btn-sm mod-matrix-delete';
    del.textContent = '✕';
    del.setAttribute('aria-label', 'Remove connection');
    del.addEventListener('click', () => this.router.removeConnection(conn.id));

    row.append(label, slider.element, depthReadout, rangeSelect, curveSelect, del);
    return row;
  }
}

/** Display label for each curve. Compact glyphs so they fit the
 *  narrow Mod Matrix column without breaking layout. */
function curveLabel(c: ModCurve): string {
  switch (c) {
    case 'linear':
      return 'Lin';
    case 'exp':
      return 'Exp';
    case 'log':
      return 'Log';
    case 's':
      return 'S';
  }
}

/** Available range multipliers for the per-connection Range picker.
 *  Covers 100× range (full → ultra-fine); intermediate steps feel like
 *  the natural "slow down the mod depth by an order of magnitude" move. */
const RANGE_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 1, label: '×1' },
  { value: 0.3, label: '×.3' },
  { value: 0.1, label: '×.1' },
  { value: 0.03, label: '×.03' },
  { value: 0.01, label: '×.01' },
];

/**
 * True when two connection snapshots have identical id / source /
 * target / param tuples in the same order — "same shape" means a
 * full row rebuild is unnecessary. Ignores depth + rangeMul since
 * those update via their controls' local state and readout, not
 * through a DOM rebuild.
 */
function sameConnectionShape(
  prev: ReadonlyArray<{ id: string; source: string; target: string; param: string }>,
  next: ReadonlyArray<{
    id: string;
    sourceModuleId: string;
    targetModuleId: string;
    targetParam: string;
  }>,
): boolean {
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i]!;
    const n = next[i]!;
    if (
      p.id !== n.id ||
      p.source !== n.sourceModuleId ||
      p.target !== n.targetModuleId ||
      p.param !== n.targetParam
    ) {
      return false;
    }
  }
  return true;
}

function formatDepth(d: number): string {
  return `${d >= 0 ? '+' : ''}${d.toFixed(2)}`;
}

/**
 * Replace a select's options with source entries grouped by category.
 * Category order follows first-appearance in the source list so the
 * "Modulators" group stays above "Per-note" stays above "Macros"
 * (matching the order sources get registered).
 */
function populateSourceSelect(
  select: HTMLSelectElement,
  sources: ReadonlyArray<{ id: string; label: string; category: string }>,
  selectedId?: string,
): void {
  select.innerHTML = '';
  const groups = new Map<string, HTMLOptGroupElement>();
  for (const s of sources) {
    let group = groups.get(s.category);
    if (!group) {
      group = document.createElement('optgroup');
      group.label = s.category;
      groups.set(s.category, group);
      select.appendChild(group);
    }
    const o = document.createElement('option');
    o.value = s.id;
    o.textContent = s.label;
    if (selectedId !== undefined && s.id === selectedId) o.selected = true;
    group.appendChild(o);
  }
}

/** Same idea as populateSourceSelect, but for the target list. */
function populateTargetSelect(
  select: HTMLSelectElement,
  targets: ReadonlyArray<ModTarget>,
  selectedValue?: string,
): void {
  select.innerHTML = '';
  const groups = new Map<string, HTMLOptGroupElement>();
  for (const t of targets) {
    let group = groups.get(t.category);
    if (!group) {
      group = document.createElement('optgroup');
      group.label = t.category;
      groups.set(t.category, group);
      select.appendChild(group);
    }
    const o = document.createElement('option');
    const value = `${t.moduleId}::${t.param}`;
    o.value = value;
    o.textContent = t.label;
    if (selectedValue !== undefined && value === selectedValue) o.selected = true;
    group.appendChild(o);
  }
}

